/**
 * Option catalogs for the "Define & Prioritize Business Goals" section of the
 * creation wizard (Step 1) and the Task Settings constraints editor.
 *
 * NOTE: the Figma frames only show the *selected* value of each dropdown, so
 * these lists are the agreed product defaults; extend/trim them here in one
 * place — every constraint UI reads from this module.
 */

/** Pillar ids match the backend `BusinessGoalPillar` enum. */
export const BUSINESS_GOAL_PILLARS = [
    {
        id: 'digital_footprint',
        title: 'Digital Footprint',
        subtitle: 'Model size, VRAM',
        iconSlug: 'pillar-digital-footprint',
        tooltip:
            'How much server memory (RAM/VRAM) and disk space the model needs to run.',
    },
    {
        id: 'cost',
        title: 'Cost',
        subtitle: 'Inference budget',
        iconSlug: 'pillar-cost',
        tooltip: 'Cost: How much you spend on compute resources or API fees per request.',
    },
    {
        id: 'performance',
        title: 'Performance',
        subtitle: 'Latency, Throughput',
        iconSlug: 'pillar-performance',
        tooltip:
            'Performance: How fast the model responds (latency) and how many concurrent requests it handles (throughput).',
    },
    {
        id: 'quality',
        title: 'Quality',
        subtitle: 'Accuracy, F-scores',
        iconSlug: 'pillar-quality',
        tooltip: "Quality: How accurate and reliable the model's answers are. This includes metrics like the F-score, a metric that balances finding all the right answers without triggering false alarms.",
    },
];

/** Default rank order: least important (index 0) -> most important. */
export const DEFAULT_PILLAR_ORDER = BUSINESS_GOAL_PILLARS.map((p) => p.id);

export const CONSTRAINT_TYPES = [
    { value: 'performance', label: 'Performance' },
    { value: 'quality', label: 'Quality' },
    { value: 'cost', label: 'Cost' },
    { value: 'footprint', label: 'Footprint' },
];

export const PERFORMANCE_METRICS = [
    { value: 'e2e_latency', label: 'E2E Latency', unit: 'ms' },
    { value: 'time_to_first_token', label: 'Time to First Token', unit: 'ms' },
    { value: 'throughput', label: 'Throughput', unit: 'req/s' },
];

export const AGGREGATIONS = [
    { value: 'p50', label: 'P50' },
    { value: 'p90', label: 'P90' },
    { value: 'p95', label: 'P95' },
    { value: 'p99', label: 'P99' },
    { value: 'average', label: 'Average' },
    { value: 'max', label: 'Max' },
];

export const LOAD_PROFILES = [
    { value: 'single_request', label: 'Single Request (1 concurrent request)' },
    { value: 'light_load', label: 'Light Load (10 concurrent requests)' },
    { value: 'heavy_load', label: 'Heavy Load (100 concurrent requests)' },
    { value: 'massive_throughput', label: 'Massive Throughput (1000 concurrent requests)' },
];

export const QUALITY_VALUE_TYPES = [
    {
        value: 'absolute_value',
        label: 'Absolute Value',
        tooltip:
            'The Specific AI model’s evaluation metric cannot be lower than the selected value',
    },
    {
        value: 'max_delta',
        label: 'Max Delta / Degradation',
        tooltip:
            'Limits how much the Specific AI model’s results can differ from the teacher’s results.',
    },
];

export const EVALUATION_METRICS = [
    { value: 'accuracy', label: 'Accuracy' },
    { value: 'f1', label: 'F1 Score' },
    { value: 'precision', label: 'Precision' },
    { value: 'recall', label: 'Recall' },
];

/** Sentinel used in `entity_types` meaning "every entity type in the task". */
export const ALL_ENTITY_TYPES = 'all';

export const MATCHING_OPTIONS = [
    { value: 'partial', label: 'Partial Matching' },
    { value: 'exact', label: 'Exact Matching' },
];

export const PRICING_METRICS = [
    { value: 'cost_per_1m_output_tokens', label: 'Cost per 1M output tokens', unit: '$/hr' },
    { value: 'cost_per_1m_input_tokens', label: 'Cost per 1M input tokens', unit: '$/hr' },
    { value: 'cost_per_hour', label: 'Cost per hour', unit: '$/hr' },
];

export const RESOURCE_METRICS = [
    { value: 'peak_active_vram', label: 'Peak Active VRAM / Memory', unit: 'GB' },
    { value: 'model_size_on_disk', label: 'Model Size on Disk', unit: 'GB' },
];

export const OPERATORS = [
    { value: '<=', label: '<=' },
    { value: '<', label: '<' },
    { value: '>=', label: '>=' },
    { value: '>', label: '>' },
    { value: '==', label: '==' },
];

/** Default unit per constraint type (quality is always a percentage). */
export const CONSTRAINT_UNITS = {
    performance: 'ms',
    quality: '%',
    cost: '$/hr',
    footprint: 'GB',
};

const METRICS_BY_TYPE = {
    performance: PERFORMANCE_METRICS,
    quality: EVALUATION_METRICS,
    cost: PRICING_METRICS,
    footprint: RESOURCE_METRICS,
};

/**
 * Default operator per constraint type: quality metrics are "higher is
 * better" (lower bound), everything else is a budget/limit (upper bound).
 */
export const DEFAULT_OPERATORS = {
    performance: '<=',
    quality: '>=',
    cost: '<=',
    footprint: '<=',
};

export const metricsForType = (constraintType) => METRICS_BY_TYPE[constraintType] || [];

/**
 * Identity of a constraint row. Performance is unique on type + metric +
 * aggregation (E2E Latency P95 ≠ E2E Latency P50); other types are unique on
 * type + metric.
 */
export const constraintIdentityKey = (c) => {
    if (c.constraint_type === 'performance') {
        return `${c.constraint_type}|${c.metric}|${c.aggregation || ''}`;
    }
    return `${c.constraint_type}|${c.metric}`;
};

const withMetric = (row, metricDef) => {
    if (!metricDef) return row;
    return {
        ...row,
        metric: metricDef.value,
        unit: metricDef.unit || row.unit,
    };
};

const identityTaken = (constraints, candidate, exceptId = null) =>
    constraints.some(
        (c) => c.id !== exceptId && constraintIdentityKey(c) === constraintIdentityKey(candidate)
    );

/** First free performance metric + aggregation pair, or null when all 18 are used. */
export const firstUnusedPerformanceCombo = (constraints = [], exceptId = null) => {
    for (const metric of PERFORMANCE_METRICS) {
        for (const agg of AGGREGATIONS) {
            if (
                !identityTaken(
                    constraints,
                    { constraint_type: 'performance', metric: metric.value, aggregation: agg.value },
                    exceptId
                )
            ) {
                return { metric, aggregation: agg.value };
            }
        }
    }
    return null;
};

/** Metrics of `constraintType` that still have a free identity (except `exceptId`). */
export const unusedMetricsForType = (constraints, constraintType, exceptId = null) => {
    if (constraintType === 'performance') {
        return PERFORMANCE_METRICS.filter((m) =>
            AGGREGATIONS.some(
                (agg) =>
                    !identityTaken(
                        constraints,
                        {
                            constraint_type: 'performance',
                            metric: m.value,
                            aggregation: agg.value,
                        },
                        exceptId
                    )
            )
        );
    }
    return metricsForType(constraintType).filter(
        (m) =>
            !identityTaken(constraints, { constraint_type: constraintType, metric: m.value }, exceptId)
    );
};

let constraintRowSeq = 0;

/** New constraint row pre-filled with the defaults shown in Figma. */
export const buildEmptyConstraint = (constraintType = 'performance') => {
    constraintRowSeq += 1;
    const base = {
        id: `constraint-${Date.now()}-${constraintRowSeq}`,
        constraint_type: constraintType,
        metric: metricsForType(constraintType)[0]?.value || '',
        aggregation: null,
        load_profile: null,
        value_type: null,
        entity_types: null,
        matching: null,
        operator: DEFAULT_OPERATORS[constraintType] || '<=',
        value: '',
        unit: CONSTRAINT_UNITS[constraintType],
    };
    if (constraintType === 'performance') {
        base.aggregation = AGGREGATIONS[2].value; // P95
        base.load_profile = LOAD_PROFILES[0].value; // Single Request
    }
    if (constraintType === 'quality') {
        base.value_type = QUALITY_VALUE_TYPES[0].value;
    }
    return withMetric(base, metricsForType(constraintType)[0]);
};

/**
 * Re-derive type-specific fields after the user switches a row's type so the
 * row never carries stale fields from the previous type. The operator resets
 * to the new type's default (e.g. quality flips to ">=") rather than
 * inheriting the previous type's choice. Prefers an identity that no sibling
 * already uses so switching type cannot silently duplicate a row.
 */
export const retypeConstraint = (row, nextType, siblings = []) => {
    const base = {
        ...buildEmptyConstraint(nextType),
        id: row.id,
        value: row.value ?? '',
    };
    if (nextType === 'performance') {
        const combo = firstUnusedPerformanceCombo(siblings, row.id);
        if (!combo) return base;
        return { ...withMetric(base, combo.metric), aggregation: combo.aggregation };
    }
    return withMetric(
        base,
        unusedMetricsForType(siblings, nextType, row.id)[0] || metricsForType(nextType)[0]
    );
};

/**
 * Validation message for an incomplete constraint row, or null when the row
 * is complete. Dropdowns are pre-filled with defaults, so in practice the
 * numeric value is the field users leave empty — but every required field is
 * checked so hydrated/edited rows can't slip through half-filled.
 */
export const constraintRowError = (c) => {
    if (c.value === '' || c.value === null || c.value === undefined) {
        return `Please enter a value (${c.constraint_type === 'cost' ? '$/hr' : c.unit || CONSTRAINT_UNITS[c.constraint_type]}) for this constraint`;
    }
    if (!c.metric || !c.operator) return 'Please fill in all constraint fields';
    if (c.constraint_type === 'performance' && (!c.aggregation || !c.load_profile)) {
        return 'Please fill in all constraint fields';
    }
    if (c.constraint_type === 'quality' && !c.value_type) {
        return 'Please fill in all constraint fields';
    }
    return null;
};

/**
 * Two rows are comparable (and can therefore conflict) only when they measure
 * the same quantity: same type + metric, plus aggregation/load profile for
 * performance and value type/entity types/matching for quality.
 */
const comparableConstraintKey = (c) => {
    const parts = [c.constraint_type, c.metric];
    if (c.constraint_type === 'performance') parts.push(c.aggregation, c.load_profile);
    if (c.constraint_type === 'quality') {
        parts.push(
            c.value_type,
            (c.entity_types || [ALL_ENTITY_TYPES]).slice().sort().join('+'),
            c.matching || ''
        );
    }
    return parts.join('|');
};

/** Feasible-value interval implied by a row's operator + numeric value. */
const constraintInterval = (c) => {
    const v = Number(c.value);
    switch (c.operator) {
        case '<=':
            return { lo: -Infinity, loInc: false, hi: v, hiInc: true };
        case '<':
            return { lo: -Infinity, loInc: false, hi: v, hiInc: false };
        case '>=':
            return { lo: v, loInc: true, hi: Infinity, hiInc: false };
        case '>':
            return { lo: v, loInc: false, hi: Infinity, hiInc: false };
        case '==':
            return { lo: v, loInc: true, hi: v, hiInc: true };
        default:
            return null;
    }
};

const intervalsDisjoint = (a, b) => {
    const lo = Math.max(a.lo, b.lo);
    const hi = Math.min(a.hi, b.hi);
    if (lo > hi) return true;
    if (lo < hi) return false;
    // Single candidate point: both intervals must include it.
    const loInc = (a.lo !== lo || a.loInc) && (b.lo !== lo || b.loInc);
    const hiInc = (a.hi !== hi || a.hiInc) && (b.hi !== hi || b.hiInc);
    return !(loInc && hiInc);
};

/** True when every value allowed by `a` is also allowed by `b` (a ⊆ b). */
const intervalSubset = (a, b) => {
    const loOk = b.lo < a.lo || (b.lo === a.lo && (b.loInc || !a.loInc));
    const hiOk = b.hi > a.hi || (b.hi === a.hi && (b.hiInc || !a.hiInc));
    return loOk && hiOk;
};

/** Short "E2E Latency (P95) <= 300 ms" style descriptor used in conflict messages. */
const shortConstraintLabel = (c) => {
    const metric = optionLabel(metricsForType(c.constraint_type), c.metric);
    const agg =
        c.constraint_type === 'performance' && c.aggregation
            ? ` (${optionLabel(AGGREGATIONS, c.aggregation)})`
            : '';
    const value =
        c.constraint_type === 'cost' ? `$${c.value}${c.unit || '/hr'}` : `${c.value} ${c.unit || ''}`;
    return `${metric}${agg} ${c.operator} ${value}`.trim();
};

/**
 * Cross-row validation: flags pairs of comparable constraints where one
 * contradicts the other (no value can satisfy both, e.g. "< 300" and "> 300")
 * or one contains the other (redundant, e.g. "<= 300" and "<= 200").
 * Returns a { rowId: message } map; the message is attached to the later row
 * of each conflicting pair. Incomplete rows are skipped — they already get
 * their own `constraintRowError` message.
 */
export const constraintConflictErrors = (constraints = []) => {
    const errors = {};
    const groups = new Map();
    constraints.forEach((c) => {
        if (constraintRowError(c) || Number.isNaN(Number(c.value))) return;
        const key = comparableConstraintKey(c);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(c);
    });
    groups.forEach((rows) => {
        for (let i = 1; i < rows.length; i += 1) {
            for (let j = 0; j < i && !errors[rows[i].id]; j += 1) {
                const a = constraintInterval(rows[j]);
                const b = constraintInterval(rows[i]);
                if (!a || !b) continue;
                const other = shortConstraintLabel(rows[j]);
                if (intervalsDisjoint(a, b)) {
                    errors[rows[i].id] =
                        `This constraint contradicts "${other}" - no model can satisfy both. Remove or adjust one of them.`;
                } else if (intervalSubset(a, b) && intervalSubset(b, a)) {
                    errors[rows[i].id] =
                        `This constraint is identical to "${other}". Remove one of them.`;
                } else if (intervalSubset(a, b) || intervalSubset(b, a)) {
                    errors[rows[i].id] =
                        `This constraint overlaps with "${other}" - one already includes the other. Remove or adjust one of them.`;
                }
            }
        }
    });
    return errors;
};

/**
 * Later rows that reuse another row's identity (type + metric, plus
 * aggregation for performance) even when the value is still empty.
 */
export const constraintDuplicateMetricErrors = (constraints = []) => {
    const errors = {};
    const seen = new Set();
    constraints.forEach((c) => {
        if (!c.metric) return;
        if (c.constraint_type === 'performance' && !c.aggregation) return;
        const key = constraintIdentityKey(c);
        if (seen.has(key)) {
            const type = optionLabel(CONSTRAINT_TYPES, c.constraint_type);
            const metric = optionLabel(metricsForType(c.constraint_type), c.metric);
            if (c.constraint_type === 'performance') {
                const agg = optionLabel(AGGREGATIONS, c.aggregation);
                errors[c.id] =
                    `A ${type} constraint for ${metric} (${agg}) already exists. Choose a different metric or aggregation, or remove this row.`;
            } else {
                errors[c.id] =
                    `A ${type} constraint for ${metric} already exists. Choose a different metric or remove this row.`;
            }
        } else {
            seen.add(key);
        }
    });
    return errors;
};

/** Short Spark toast shown when Next/Save is blocked by a duplicate identity. */
export const DUPLICATE_CONSTRAINT_TOAST = {
    title: 'Duplicate constraint',
    content: 'Type, metric, and aggregation must be unique',
};

/**
 * Combined per-row errors: duplicate identity first, then incomplete fields,
 * then contradicting/redundant comparable rows.
 */
export const collectConstraintErrors = (constraints = []) => {
    const errors = {};
    const duplicates = constraintDuplicateMetricErrors(constraints);
    constraints.forEach((c) => {
        const msg = duplicates[c.id] || constraintRowError(c);
        if (msg) errors[c.id] = msg;
    });
    Object.entries(constraintConflictErrors(constraints)).forEach(([id, message]) => {
        if (!errors[id]) errors[id] = message;
    });
    return errors;
};

/** Serialize UI rows into the backend `constraints` payload. */
export const constraintsToPayload = (constraints = []) =>
    constraints
        .filter((c) => c.value !== '' && c.value !== null && c.value !== undefined)
        .map((c) => ({
            id: c.id,
            constraint_type: c.constraint_type,
            metric: c.metric,
            aggregation: c.constraint_type === 'performance' ? c.aggregation : null,
            load_profile: c.constraint_type === 'performance' ? c.load_profile : null,
            value_type: c.constraint_type === 'quality' ? c.value_type : null,
            entity_types: c.constraint_type === 'quality' ? c.entity_types || null : null,
            matching: c.constraint_type === 'quality' ? c.matching || null : null,
            operator: c.operator,
            value: Number(c.value),
            unit: c.unit || CONSTRAINT_UNITS[c.constraint_type] || '',
        }));

/** Pillar id -> display title ("digital_footprint" -> "Digital Footprint"). */
export const pillarTitle = (pillarId) =>
    BUSINESS_GOAL_PILLARS.find((p) => p.id === pillarId)?.title || pillarId;

const optionLabel = (options, value) =>
    options.find((o) => o.value === value)?.label || value || '';

/**
 * Human-readable one-liner for a backend constraint dict — used by the
 * read-only Model Details display, e.g.
 * "Performance: E2E Latency (P95) <= 500 ms @ Massive Throughput (1000 concurrent requests)".
 */
export const formatConstraintForDisplay = (c) => {
    if (!c) return '';
    const type = optionLabel(CONSTRAINT_TYPES, c.constraint_type);
    const metric = optionLabel(metricsForType(c.constraint_type), c.metric);
    const isCost = c.constraint_type === 'cost';
    const valueUnit = isCost ? `$${c.value} ${c.unit || '/hr'}` : `${c.value} ${c.unit || ''}`;
    const parts = [`${type}:`, metric];
    if (c.constraint_type === 'performance' && c.aggregation) {
        parts.push(`(${optionLabel(AGGREGATIONS, c.aggregation)})`);
    }
    if (c.constraint_type === 'quality') {
        if (c.value_type) parts.push(`(${optionLabel(QUALITY_VALUE_TYPES, c.value_type)})`);
        if (c.entity_types?.length && c.entity_types[0] !== ALL_ENTITY_TYPES) {
            parts.push(`[${c.entity_types.join(', ')}]`);
        }
        if (c.matching) parts.push(`(${optionLabel(MATCHING_OPTIONS, c.matching)})`);
    }
    parts.push(c.operator, valueUnit.trim());
    if (c.constraint_type === 'performance' && c.load_profile) {
        parts.push(`@ ${optionLabel(LOAD_PROFILES, c.load_profile)}`);
    }
    return parts.join(' ');
};

/** Hydrate backend constraint dicts into editable UI rows. */
export const constraintsFromBackend = (constraints = []) =>
    (constraints || []).map((c) => ({
        id: c.id || buildEmptyConstraint(c.constraint_type).id,
        constraint_type: c.constraint_type,
        metric: c.metric,
        aggregation: c.aggregation ?? null,
        load_profile: c.load_profile ?? null,
        value_type: c.value_type ?? null,
        entity_types: c.entity_types ?? null,
        matching: c.matching ?? null,
        operator: c.operator || DEFAULT_OPERATORS[c.constraint_type] || '<=',
        value: c.value ?? '',
        unit: c.unit || CONSTRAINT_UNITS[c.constraint_type] || '',
    }));
